import React, { useState, useEffect } from 'react';
import { 
  Send, ArrowRight, Building2, User, Globe2, CreditCard, 
  ChevronDown, AlertCircle, CheckCircle2, Loader2, ArrowLeft,
  Search, Clock, Star
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Supported currencies with their details
const CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', flag: '🇨🇭' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
];

// Transfer types
const TRANSFER_TYPES = [
  { id: 'local', name: 'Local Transfer', description: 'Same currency, same country', icon: Building2, fee: 'Free', time: 'Instant' },
  { id: 'sepa', name: 'SEPA Transfer', description: 'Within Europe', icon: Globe2, fee: '€0.50', time: '1-2 days' },
  { id: 'swift', name: 'International (SWIFT)', description: 'Worldwide', icon: Globe2, fee: '€15.00', time: '2-5 days' },
];

const SendMoney = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [step, setStep] = useState(1); // 1: Amount, 2: Recipient, 3: Confirm
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const [transferType, setTransferType] = useState('sepa');
  const [recipient, setRecipient] = useState({
    name: '',
    iban: '',
    bic: '',
    bankName: '',
    reference: '',
    saveAsBeneficiary: false
  });
  const [loading, setLoading] = useState(false);
  const [recentBeneficiaries, setRecentBeneficiaries] = useState([]);
  const [showBeneficiaries, setShowBeneficiaries] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Theme colors
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const cardBg = isDark ? 'bg-[#111111]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const inputBg = isDark ? 'bg-white/5' : 'bg-gray-50';

  // Mock exchange rates (would come from API in production)
  const exchangeRates = {
    EUR: { GBP: 0.85, USD: 1.08, CHF: 0.95, CAD: 1.47, AUD: 1.65 },
    GBP: { EUR: 1.18, USD: 1.27, CHF: 1.12, CAD: 1.73, AUD: 1.94 },
    USD: { EUR: 0.93, GBP: 0.79, CHF: 0.88, CAD: 1.36, AUD: 1.53 },
  };

  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  const fetchBeneficiaries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/banking/beneficiaries`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRecentBeneficiaries(response.data.beneficiaries || []);
    } catch (error) {
      // Demo beneficiaries
      setRecentBeneficiaries([
        { id: '1', name: 'John Smith', iban: 'GB82 WEST 1234 5698 7654 32', bankName: 'HSBC UK', isFavorite: true },
        { id: '2', name: 'Marie Dupont', iban: 'FR76 3000 6000 0112 3456 7890 189', bankName: 'BNP Paribas', isFavorite: false },
        { id: '3', name: 'Global Trading Ltd', iban: 'DE89 3704 0044 0532 0130 00', bankName: 'Commerzbank', isFavorite: true },
      ]);
    }
  };

  const selectedCurrency = CURRENCIES.find(c => c.code === currency);
  const selectedTransferType = TRANSFER_TYPES.find(t => t.id === transferType);

  const calculateFee = () => {
    if (transferType === 'local') return 0;
    if (transferType === 'sepa') return 0.50;
    return 15.00;
  };

  const totalAmount = parseFloat(amount || 0) + calculateFee();

  const validateIBAN = (iban) => {
    // Basic IBAN validation (simplified)
    const cleanIBAN = iban.replace(/\s/g, '').toUpperCase();
    return cleanIBAN.length >= 15 && cleanIBAN.length <= 34;
  };

  const handleNext = () => {
    if (step === 1) {
      if (!amount || parseFloat(amount) <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!recipient.name.trim()) {
        toast.error('Please enter recipient name');
        return;
      }
      if (!validateIBAN(recipient.iban)) {
        toast.error('Please enter a valid IBAN');
        return;
      }
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    else navigate(-1);
  };

  const selectBeneficiary = (beneficiary) => {
    setRecipient({
      ...recipient,
      name: beneficiary.name,
      iban: beneficiary.iban,
      bankName: beneficiary.bankName
    });
    setShowBeneficiaries(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/transfers/fiat`, {
        amount: parseFloat(amount),
        currency: currency,
        transfer_type: transferType,
        recipient_name: recipient.name,
        recipient_iban: recipient.iban,
        recipient_bic: recipient.bic,
        bank_name: recipient.bankName,
        reference: recipient.reference,
        save_beneficiary: recipient.saveAsBeneficiary
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      toast.success('Transfer initiated successfully!');
      navigate('/transactions');
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error('Transfer failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredBeneficiaries = recentBeneficiaries.filter(b => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.iban.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="send-money-page" className="min-h-screen py-6 px-3 md:px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={handleBack}
            className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} transition-colors`}
          >
            <ArrowLeft className={`w-5 h-5 ${textPrimary}`} />
          </button>
          <div>
            <h1 className={`text-2xl font-bold ${textPrimary}`}>Send Money</h1>
            <p className={`${textMuted} text-sm`}>Transfer funds to anyone, anywhere</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s 
                  ? 'bg-purple-600 text-white' 
                  : isDark ? 'bg-white/10 text-white/40' : 'bg-gray-200 text-gray-400'
              }`}>
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-purple-600' : isDark ? 'bg-white/10' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Amount & Currency */}
        {step === 1 && (
          <div className={`${cardBg} border ${cardBorder} rounded-2xl p-6`}>
            <h2 className={`text-lg font-semibold ${textPrimary} mb-4`}>How much do you want to send?</h2>

            {/* Amount Input */}
            <div className={`${inputBg} border ${cardBorder} rounded-xl p-4 mb-4`}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowCurrencyPicker(!showCurrencyPicker)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-white hover:bg-gray-50'} border ${cardBorder} transition-colors`}
                >
                  <span className="text-xl">{selectedCurrency?.flag}</span>
                  <span className={`font-medium ${textPrimary}`}>{currency}</span>
                  <ChevronDown className={`w-4 h-4 ${textMuted}`} />
                </button>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={`flex-1 text-3xl font-bold ${textPrimary} bg-transparent outline-none text-right`}
                />
              </div>
            </div>

            {/* Currency Picker Dropdown */}
            {showCurrencyPicker && (
              <div className={`${cardBg} border ${cardBorder} rounded-xl mb-4 overflow-hidden`}>
                {CURRENCIES.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => {
                      setCurrency(c.code);
                      setShowCurrencyPicker(false);
                    }}
                    className={`w-full flex items-center gap-3 p-3 ${
                      currency === c.code 
                        ? 'bg-purple-500/10' 
                        : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                    } transition-colors`}
                  >
                    <span className="text-xl">{c.flag}</span>
                    <div className="text-left">
                      <div className={`font-medium ${textPrimary}`}>{c.code}</div>
                      <div className={`text-xs ${textMuted}`}>{c.name}</div>
                    </div>
                    {currency === c.code && (
                      <CheckCircle2 className="w-5 h-5 text-purple-500 ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Transfer Type Selection */}
            <div className="mb-6">
              <label className={`text-sm ${textMuted} block mb-2`}>Transfer Type</label>
              <div className="space-y-2">
                {TRANSFER_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setTransferType(type.id)}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                        transferType === type.id
                          ? 'border-purple-500 bg-purple-500/10'
                          : `${cardBorder} ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${
                          transferType === type.id ? 'bg-purple-500/20' : isDark ? 'bg-white/10' : 'bg-gray-100'
                        } flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${transferType === type.id ? 'text-purple-500' : textMuted}`} />
                        </div>
                        <div className="text-left">
                          <div className={`font-medium ${textPrimary}`}>{type.name}</div>
                          <div className={`text-xs ${textMuted}`}>{type.description}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${type.fee === 'Free' ? 'text-green-500' : textPrimary}`}>{type.fee}</div>
                        <div className={`text-xs ${textMuted} flex items-center gap-1`}>
                          <Clock className="w-3 h-3" />
                          {type.time}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Summary */}
            {amount && (
              <div className={`${inputBg} rounded-xl p-4 mb-6`}>
                <div className="flex justify-between mb-2">
                  <span className={textMuted}>Amount</span>
                  <span className={textPrimary}>{selectedCurrency?.symbol}{parseFloat(amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className={textMuted}>Fee</span>
                  <span className={calculateFee() === 0 ? 'text-green-500' : textPrimary}>
                    {calculateFee() === 0 ? 'Free' : `${selectedCurrency?.symbol}${calculateFee().toFixed(2)}`}
                  </span>
                </div>
                <div className={`flex justify-between pt-2 border-t ${cardBorder}`}>
                  <span className={`font-medium ${textPrimary}`}>Total</span>
                  <span className={`font-bold text-lg ${textPrimary}`}>{selectedCurrency?.symbol}{totalAmount.toFixed(2)}</span>
                </div>
              </div>
            )}

            <button
              onClick={handleNext}
              disabled={!amount || parseFloat(amount) <= 0}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-4 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 2: Recipient Details */}
        {step === 2 && (
          <div className={`${cardBg} border ${cardBorder} rounded-2xl p-6`}>
            <h2 className={`text-lg font-semibold ${textPrimary} mb-4`}>Who are you sending to?</h2>

            {/* Recent Beneficiaries */}
            {recentBeneficiaries.length > 0 && (
              <div className="mb-6">
                <button
                  onClick={() => setShowBeneficiaries(!showBeneficiaries)}
                  className={`w-full flex items-center justify-between p-3 ${inputBg} rounded-xl`}
                >
                  <div className="flex items-center gap-2">
                    <Star className={`w-5 h-5 ${textMuted}`} />
                    <span className={textPrimary}>Select from saved beneficiaries</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 ${textMuted} ${showBeneficiaries ? 'rotate-180' : ''} transition-transform`} />
                </button>

                {showBeneficiaries && (
                  <div className={`mt-2 border ${cardBorder} rounded-xl overflow-hidden`}>
                    <div className="p-3">
                      <div className="relative">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
                        <input
                          type="text"
                          placeholder="Search beneficiaries..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className={`w-full ${inputBg} ${textPrimary} rounded-lg pl-10 pr-4 py-2 text-sm outline-none border ${cardBorder} focus:border-purple-500`}
                        />
                      </div>
                    </div>
                    {filteredBeneficiaries.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => selectBeneficiary(b)}
                        className={`w-full flex items-center justify-between p-3 ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'} flex items-center justify-center`}>
                            <User className={`w-5 h-5 ${textMuted}`} />
                          </div>
                          <div className="text-left">
                            <div className={`font-medium ${textPrimary} flex items-center gap-2`}>
                              {b.name}
                              {b.isFavorite && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                            </div>
                            <div className={`text-xs font-mono ${textMuted}`}>{b.iban.substring(0, 20)}...</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recipient Name */}
            <div className="mb-4">
              <label className={`text-sm ${textMuted} block mb-2`}>Recipient Name</label>
              <div className="relative">
                <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${textMuted}`} />
                <input
                  type="text"
                  value={recipient.name}
                  onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                  placeholder="Full name of recipient"
                  className={`w-full ${inputBg} border ${cardBorder} ${textPrimary} rounded-xl pl-12 pr-4 py-4 outline-none focus:border-purple-500`}
                />
              </div>
            </div>

            {/* IBAN */}
            <div className="mb-4">
              <label className={`text-sm ${textMuted} block mb-2`}>IBAN</label>
              <div className="relative">
                <CreditCard className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${textMuted}`} />
                <input
                  type="text"
                  value={recipient.iban}
                  onChange={(e) => setRecipient({ ...recipient, iban: e.target.value.toUpperCase() })}
                  placeholder="e.g. GB82 WEST 1234 5698 7654 32"
                  className={`w-full ${inputBg} border ${cardBorder} ${textPrimary} font-mono rounded-xl pl-12 pr-4 py-4 outline-none focus:border-purple-500`}
                />
              </div>
              {recipient.iban && !validateIBAN(recipient.iban) && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Please enter a valid IBAN
                </p>
              )}
            </div>

            {/* BIC (Optional) */}
            <div className="mb-4">
              <label className={`text-sm ${textMuted} block mb-2`}>BIC/SWIFT Code (Optional)</label>
              <input
                type="text"
                value={recipient.bic}
                onChange={(e) => setRecipient({ ...recipient, bic: e.target.value.toUpperCase() })}
                placeholder="e.g. MIDLGB22"
                className={`w-full ${inputBg} border ${cardBorder} ${textPrimary} font-mono rounded-xl px-4 py-4 outline-none focus:border-purple-500`}
              />
            </div>

            {/* Bank Name (Optional) */}
            <div className="mb-4">
              <label className={`text-sm ${textMuted} block mb-2`}>Bank Name (Optional)</label>
              <div className="relative">
                <Building2 className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${textMuted}`} />
                <input
                  type="text"
                  value={recipient.bankName}
                  onChange={(e) => setRecipient({ ...recipient, bankName: e.target.value })}
                  placeholder="e.g. HSBC UK"
                  className={`w-full ${inputBg} border ${cardBorder} ${textPrimary} rounded-xl pl-12 pr-4 py-4 outline-none focus:border-purple-500`}
                />
              </div>
            </div>

            {/* Reference */}
            <div className="mb-6">
              <label className={`text-sm ${textMuted} block mb-2`}>Reference/Note (Optional)</label>
              <input
                type="text"
                value={recipient.reference}
                onChange={(e) => setRecipient({ ...recipient, reference: e.target.value })}
                placeholder="e.g. Invoice #12345"
                maxLength={140}
                className={`w-full ${inputBg} border ${cardBorder} ${textPrimary} rounded-xl px-4 py-4 outline-none focus:border-purple-500`}
              />
              <p className={`text-xs ${textMuted} mt-1`}>{recipient.reference.length}/140 characters</p>
            </div>

            {/* Save Beneficiary */}
            <label className={`flex items-center gap-3 mb-6 cursor-pointer`}>
              <input
                type="checkbox"
                checked={recipient.saveAsBeneficiary}
                onChange={(e) => setRecipient({ ...recipient, saveAsBeneficiary: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className={textPrimary}>Save as beneficiary for future transfers</span>
            </label>

            <button
              onClick={handleNext}
              disabled={!recipient.name || !validateIBAN(recipient.iban)}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-4 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className={`${cardBg} border ${cardBorder} rounded-2xl p-6`}>
            <h2 className={`text-lg font-semibold ${textPrimary} mb-4`}>Confirm Transfer</h2>

            {/* Transfer Summary */}
            <div className={`${inputBg} rounded-xl p-4 mb-4`}>
              <div className="flex items-center justify-center mb-4">
                <div className="text-center">
                  <p className={`text-4xl font-bold ${textPrimary}`}>
                    {selectedCurrency?.symbol}{parseFloat(amount).toFixed(2)}
                  </p>
                  <p className={`${textMuted}`}>{currency}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className={`w-10 h-10 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} flex items-center justify-center`}>
                  <User className={`w-5 h-5 ${textMuted}`} />
                </div>
                <ArrowRight className={`w-5 h-5 ${textMuted}`} />
                <div className={`w-10 h-10 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} flex items-center justify-center`}>
                  <Building2 className={`w-5 h-5 ${textMuted}`} />
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="space-y-3 mb-6">
              <div className={`flex justify-between p-3 ${inputBg} rounded-xl`}>
                <span className={textMuted}>To</span>
                <span className={`font-medium ${textPrimary}`}>{recipient.name}</span>
              </div>
              <div className={`flex justify-between p-3 ${inputBg} rounded-xl`}>
                <span className={textMuted}>IBAN</span>
                <span className={`font-mono text-sm ${textPrimary}`}>{recipient.iban}</span>
              </div>
              {recipient.bankName && (
                <div className={`flex justify-between p-3 ${inputBg} rounded-xl`}>
                  <span className={textMuted}>Bank</span>
                  <span className={textPrimary}>{recipient.bankName}</span>
                </div>
              )}
              <div className={`flex justify-between p-3 ${inputBg} rounded-xl`}>
                <span className={textMuted}>Transfer Type</span>
                <span className={textPrimary}>{selectedTransferType?.name}</span>
              </div>
              <div className={`flex justify-between p-3 ${inputBg} rounded-xl`}>
                <span className={textMuted}>Fee</span>
                <span className={calculateFee() === 0 ? 'text-green-500' : textPrimary}>
                  {calculateFee() === 0 ? 'Free' : `${selectedCurrency?.symbol}${calculateFee().toFixed(2)}`}
                </span>
              </div>
              <div className={`flex justify-between p-3 ${inputBg} rounded-xl`}>
                <span className={textMuted}>Estimated Arrival</span>
                <span className={textPrimary}>{selectedTransferType?.time}</span>
              </div>
              {recipient.reference && (
                <div className={`flex justify-between p-3 ${inputBg} rounded-xl`}>
                  <span className={textMuted}>Reference</span>
                  <span className={textPrimary}>{recipient.reference}</span>
                </div>
              )}
            </div>

            {/* Total */}
            <div className={`${isDark ? 'bg-purple-500/10' : 'bg-purple-50'} rounded-xl p-4 mb-6`}>
              <div className="flex justify-between">
                <span className={`font-medium ${textPrimary}`}>Total Debit</span>
                <span className={`font-bold text-xl ${textPrimary}`}>{selectedCurrency?.symbol}{totalAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Warning */}
            <div className={`flex items-start gap-3 p-4 ${isDark ? 'bg-yellow-500/10' : 'bg-yellow-50'} rounded-xl mb-6`}>
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className={`text-sm ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                Please verify all details are correct. Transfers cannot be reversed once initiated.
              </p>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium py-4 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Confirm & Send
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendMoney;
